import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, 
  Plus, 
  Loader2, 
  Clock, 
  Building, 
  User, 
  Sparkles,
  Trash2,
  Search,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const SOPList = () => {
  const navigate = useNavigate();
  const [sops, setSops] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenDialog, setShowGenDialog] = useState(false);
  const [selectedCapture, setSelectedCapture] = useState('');
  const [sopName, setSopName] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sopsRes, capturesRes] = await Promise.all([
        api.get('/sops'),
        api.get('/process-captures')
      ]);
      setSops(sopsRes.data);
      setCaptures(capturesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCapture) {
      toast.error('Please select a process capture');
      return;
    }
    setGenerating(true);
    try {
      const response = await api.post('/sops/generate', {
        capture_id: selectedCapture,
        name: sopName || undefined
      });
      toast.success('SOP generated!');
      setShowGenDialog(false);
      setSelectedCapture('');
      setSopName('');
      navigate(`/sop-generator/${response.data.id}`);
    } catch (error) {
      console.error('Failed to generate SOP:', error);
      toast.error('Failed to generate SOP');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this SOP?')) return;
    try {
      await api.delete(`/sops/${id}`);
      setSops(prev => prev.filter(s => s.id !== id));
      toast.success('SOP deleted');
    } catch (error) {
      toast.error('Failed to delete SOP');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-emerald-100 text-emerald-700';
      case 'Draft': return 'bg-slate-100 text-slate-700';
      case 'Archived': return 'bg-amber-100 text-amber-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const filtered = sops.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.department?.toLowerCase().includes(search.toLowerCase())
  );

  // Captures that have steps (eligible for SOP generation)
  const eligibleCaptures = captures.filter(c => c.steps?.length > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sop-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            SOP Generator
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Generate Standard Operating Procedures from your process captures
          </p>
        </div>
        <Button
          onClick={() => setShowGenDialog(true)}
          className="bg-[#235894] hover:bg-[#1E4B80]"
          disabled={eligibleCaptures.length === 0}
          data-testid="generate-sop-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate SOP
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search SOPs..."
          className="pl-9"
          data-testid="sop-search"
        />
      </div>

      {/* SOP List */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {sops.length === 0 ? 'No SOPs yet' : 'No results found'}
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              {sops.length === 0
                ? 'Generate your first SOP from a process capture'
                : 'Try a different search term'
              }
            </p>
            {sops.length === 0 && eligibleCaptures.length > 0 && (
              <Button onClick={() => setShowGenDialog(true)} className="bg-[#235894] hover:bg-[#1E4B80]">
                <Sparkles className="w-4 h-4 mr-2" /> Generate First SOP
              </Button>
            )}
            {eligibleCaptures.length === 0 && (
              <p className="text-sm text-slate-400 mt-2">
                Record a process capture first to generate an SOP
              </p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((sop) => (
            <Card
              key={sop.id}
              className="cursor-pointer hover:shadow-md transition-shadow group"
              onClick={() => navigate(`/sop-generator/${sop.id}`)}
              data-testid={`sop-card-${sop.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-base truncate">{sop.name}</CardTitle>
                  </div>
                  <Badge className={getStatusColor(sop.status)}>{sop.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {sop.purpose && (
                  <p className="text-sm text-slate-600 line-clamp-2">{sop.purpose}</p>
                )}
                <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                  {sop.department && (
                    <span className="flex items-center gap-1">
                      <Building className="w-3 h-3" /> {sop.department}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {sop.steps?.length || 0} steps
                  </span>
                  <span className="flex items-center gap-1">
                    <User className="w-3 h-3" /> {sop.created_by_name}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-xs text-slate-400">
                    v{sop.version} | {new Date(sop.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); navigate(`/sop-generator/${sop.id}`); }}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => handleDelete(sop.id, e)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Generate Dialog */}
      <Dialog open={showGenDialog} onOpenChange={setShowGenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#26B8A7]" />
              Generate SOP from Process Capture
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Process Capture *</Label>
              <Select value={selectedCapture} onValueChange={setSelectedCapture}>
                <SelectTrigger data-testid="capture-select">
                  <SelectValue placeholder="Select a process capture" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleCaptures.map((cap) => (
                    <SelectItem key={cap.id} value={cap.id}>
                      {cap.name} ({cap.steps?.length} steps)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>SOP Name (optional)</Label>
              <Input
                value={sopName}
                onChange={(e) => setSopName(e.target.value)}
                placeholder="Auto-generated if left blank"
                data-testid="sop-name-input"
              />
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              AI will analyze the capture's steps and screenshots to generate a professional SOP with purpose, scope, prerequisites, and detailed step-by-step instructions.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleGenerate}
              disabled={generating || !selectedCapture}
              className="bg-[#235894] hover:bg-[#1E4B80]"
              data-testid="confirm-generate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {generating ? 'Generating...' : 'Generate SOP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SOPList;
